// Warm the audio cache for the NEXT listContext item while the current one
// plays, so advanceListContext's resolveTrackSrc lands on warm caches and the
// between-track gap stays sub-second (measured: ~5-7s cold vs ~0.7s warm).
//
// Cache-warming ONLY — fire-and-forget, non-priority (never competes with the
// playing track's own download; the 2-slot pool + priority lane in main.js
// already arbitrate), deduped end-to-end (resolveTrackLinkLazy inflight map +
// ensureTrackAudioFile inflight map). Failures are invisible by design: the
// advance path re-runs the same calls with its own error handling, so this
// module must never toast, throw, or touch player state.

import { getCachedYoutubeId, resolveTrackLinkLazy } from './lazyResolveCache';
import { entityId } from './entityId';
import { isSamplesOnly } from './webOnly';

export async function prefetchTrackAudio(item) {
    // Samples-only platforms never download; blocked tracks never full-play.
    if (!item || isSamplesOnly || item.playbackBlocked === true) return;
    if (!window.electron?.djpal?.getTrackAudio) return;

    const rawYt = item.youtubeId || item.youtube_id || null;
    let youtubeId = rawYt && rawYt !== '-1' ? rawYt : null;
    let soundcloudUrl = item.soundcloudUrl || item.soundcloud_url || null;
    // Queue entries (Play All, Next Up) carry their link INSIDE the
    // /lazy-audio URL (yt= / sc= / tid=) — read it from there when the entry
    // has no top-level fields, so a queue prefetch warms the same file the
    // element will ask the audio server for.
    let lazyTid = null;
    if (!youtubeId && !soundcloudUrl && String(item.musicSrc || '').includes('/lazy-audio')) {
        try {
            const p = new URL(item.musicSrc).searchParams;
            const yt = p.get('yt');
            if (yt && yt !== '-1') youtubeId = yt;
            else if (p.get('sc')) soundcloudUrl = p.get('sc');
            else lazyTid = p.get('tid') || null;
        } catch (_) { /* malformed — fall through */ }
    }

    if (!youtubeId && !soundcloudUrl) {
        const trackId = entityId(item) || lazyTid;
        if (!trackId) return;
        // Same lazy resolve the advance path fires at track end — moved
        // earlier so its ~1s cost overlaps playback instead of the gap.
        // Seeds the shared renderer cache, so the advance's own call is a
        // cache hit even if this download prefetch doesn't finish in time.
        youtubeId = getCachedYoutubeId(trackId);
        if (!youtubeId) {
            const result = await resolveTrackLinkLazy({ ...item, id: trackId });
            const resolved = result?.youtubeId || null;
            youtubeId = resolved && resolved !== '-1' ? resolved : null;
        }
    }

    const url = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : soundcloudUrl;
    if (!url) return;
    try {
        window.electron.djpal.getTrackAudio(url, { priority: false })?.catch?.(() => {});
    } catch (_) { /* best-effort */ }
}
